/**
 * Pure helpers + shared types for the actor identity sections.
 *
 * Extracted from the former InfoTab god component so each section composes
 * against the same primitives without re-inventing formatting or parsing.
 */

import type { BlackboardInstance } from "../blackboardModels";

// ---------------------------------------------------------------------------
// Token / duration formatting
// ---------------------------------------------------------------------------

export function formatTokenCount(n: number): string {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1)}M`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return n.toLocaleString();
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (seconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
}

// ---------------------------------------------------------------------------
// Slider fill (used by OverrideControls compact-threshold range)
// ---------------------------------------------------------------------------

export function sliderFillPct(value: number, min: number, max: number): number {
  if (max <= min) {
    return 0;
  }
  const pct = ((value - min) / (max - min)) * 100;
  return Math.max(0, Math.min(100, pct));
}

export function sliderFillStyle(
  value: number,
  min: number,
  max: number,
): React.CSSProperties & { "--slider-fill": string } {
  return { "--slider-fill": `${sliderFillPct(value, min, max)}%` };
}

// ---------------------------------------------------------------------------
// Status / cache-break derivation
// ---------------------------------------------------------------------------

export function mapStatus(status: BlackboardInstance["status"]): string {
  switch (status) {
    case "running": {
      return "running";
    }
    case "waiting": {
      return "pending";
    }
    case "killed": {
      return "offline";
    }
  }
}

/**
 * Returns true when the identity prompt was saved after the actor was last
 * parked — meaning the system prompt cache will break on the next wakeup.
 * Only relevant for parked (waiting) actors.
 */
export function isStaleCacheBreak(instance: BlackboardInstance): boolean {
  if (instance.status !== "waiting") {
    return false;
  }
  if (!instance.systemPromptEditedAt || !instance.lastParkedAt) {
    return false;
  }
  return (
    new Date(instance.systemPromptEditedAt) > new Date(instance.lastParkedAt)
  );
}

/** Fallback when overrides aren't loaded yet; matches the runtime fallback. */
export const COMPACT_GROWTH_THRESHOLD_FALLBACK = 20_000;

// ---------------------------------------------------------------------------
// Teams bind-link parsing (shared by ChannelManager + IdentityTeamsSection)
// ---------------------------------------------------------------------------

export interface TeamsBindLink {
  expiresAt: string | null;
  mintedAt: string;
  token: string | null;
  url: string;
}

function hasProperty<K extends string>(
  value: unknown,
  key: K,
): value is Record<K, unknown> {
  return typeof value === "object" && value !== null && key in value;
}

export function readOptionalStringProperty<K extends string>(
  value: unknown,
  key: K,
): string | null {
  if (!hasProperty(value, key)) {
    return null;
  }
  return typeof value[key] === "string" ? value[key] : null;
}

export function parseTeamsBindLink(value: unknown): TeamsBindLink | null {
  const url = readOptionalStringProperty(value, "url");
  if (!url) {
    return null;
  }
  return {
    expiresAt: readOptionalStringProperty(value, "expiresAt"),
    mintedAt: new Date().toISOString(),
    token: readOptionalStringProperty(value, "token"),
    url,
  };
}

export function formatBindLinkMintedLine(link: TeamsBindLink): string {
  const minted = new Date(link.mintedAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (!link.expiresAt) {
    return `Minted at ${minted} · Single-use · expires after first bind`;
  }
  return `Minted at ${minted} · Expires in ${formatExpiryDistance(
    link.expiresAt,
    link.mintedAt,
  )}`;
}

export function formatExpiryDistance(expiresAt: string, from: string): string {
  const expires = new Date(expiresAt).getTime();
  const minted = new Date(from).getTime();
  const remainingMs = Math.max(0, expires - minted);
  const minutes = Math.max(1, Math.round(remainingMs / 60_000));
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const extraMinutes = minutes % 60;
  return extraMinutes === 0 ? `${hours}h` : `${hours}h ${extraMinutes}m`;
}
